import { ILangTextValidation, IPagination, IPaginationLight, IResponse, IValidation, TTranslate } from 'src/types/main';
import { UpdateLevel } from '~/mobx/appStore/types';

export const isLocalHost: boolean = window.location.hostname === 'localhost';

export enum LOCALES {
  EN = 'en',
  AR = 'ar',
  EN_UPPERCASE = 'EN',
  AR_UPPERCASE = 'AR',
}

export const LOCALES_DOUBLE = {
  en: LOCALES.EN + '-' + LOCALES.EN_UPPERCASE,
  ar: LOCALES.AR + '-' + LOCALES.AR_UPPERCASE,
};

export enum API_METHOD {
  GET = 'get',
  POST = 'post',
  PUT = 'put',
  DELETE = 'delete',
  PATCH = 'patch',
}

export enum ShortCode {
  CLIENT_NAME = '%$user_name%',
  ORDER_NUMBER = '%$order_number%',
  ORDER_ID = '%$order_id%',
  UPDATE_LOCATION = '%$update_location%',
  APP_LINK = '%$app_link%',
  DAYS = '%$days%',
  OTP = '%$otp%',
  BALANCE_TYPE = '%$balance_type%',
  BALANCE_AMOUNT = '%$balance_amount%',
  VALIDITY = '%$validity%',
  SMS_ORDER_LINK = '%$sms_order_link%',
}

export enum RELEASES {
  PRODUCTION = 'production',
  RELEASE = 'release',
  DEVELOPMENT = 'development',
}

export const WINDOW_SIZES = {
  xs: 575,
  sm: 768,
  md: 991,
  lg: 1280,
  xl: 2000,
  // xxl: 991,
};

export const defaultPagination: IPagination<any> = {
  data: [],
  current_page: 0,
  last_page: 0,
  per_page: 10,
  from: 0,
  to: 0,
  total: 0,
  // url's
  path: '',
  first_page_url: '',
  last_page_url: '',
  next_page_url: null,
  prev_page_url: null,
  //
  pages: [],
  loading: false,
  refreshing: false,
};

export const defaultPaginationLight: IPaginationLight = {
  current_page: 1,
  last_page: 1,
  per_page: 10,
  total: 0,
};

export const defaultResponse: IResponse<any> = {
  result: false,
  message: null,
  data: null,
};

export enum SORTING_DIRECTION {
  ASCENDING = 'asc',
  DESCENDING = 'desc',
}

export const OPPOSITE_SORTING_DIRECTION = {
  [SORTING_DIRECTION.ASCENDING]: SORTING_DIRECTION.DESCENDING,
  [SORTING_DIRECTION.DESCENDING]: SORTING_DIRECTION.ASCENDING,
};

export enum PAYMENT_TYPES {
  CASH = 0,
  ONLINE_PAYMENT = 1,
  TERMINAL = 3,
  JAHEZ = 4,
  HUNGER_STATION = 8,
  CAREEM = 5,
  DOOK_CASH = 6,
  DOOK_ONLINE_PAYMENT = 7,
  TO_YOU = 9,
  KEETA = 10,
}

export const paymentTypesLangKeys: Record<PAYMENT_TYPES, string> = {
  [PAYMENT_TYPES.CASH]: 'cash',
  [PAYMENT_TYPES.ONLINE_PAYMENT]: 'Online payment',
  [PAYMENT_TYPES.TERMINAL]: 'terminal',
  [PAYMENT_TYPES.JAHEZ]: 'Jahez',
  [PAYMENT_TYPES.CAREEM]: 'Careem',
  [PAYMENT_TYPES.DOOK_CASH]: 'dook_cash',
  [PAYMENT_TYPES.DOOK_ONLINE_PAYMENT]: 'dook_online',
  [PAYMENT_TYPES.HUNGER_STATION]: 'HungerStation',
  [PAYMENT_TYPES.TO_YOU]: 'ToYou',
  [PAYMENT_TYPES.KEETA]: 'Keeta',
};

export const PER_PAGE = {
  DEFAULT: 10,
  MEDIUM: 15,
  LARGE: 25,
  MAXIMUM: 100000,
} as const;

export const DEBOUNCE_TIME = {
  SMALL: 100,
  MEDIUM: 300,
  LARGE: 700,
} as const;

export enum APP_STATE {
  CLOSED,
  OPENED,
}

export const maxPushNotificationSubjectLength: number = 65;
export const maxPushNotificationMessageLength: number = 240;

// used if an error occurred while loading image
export const defaultImageSrc: string = 'https://osbornegroupcre.com/wp-content/uploads/2016/02/missing-image-640x360.png';

export enum FORM_MODE {
  HIDDEN,
  VIEW,
  EDIT,
  CREATE,
}

export const allOptionsValue = 'all_options';

export enum AVAILABILITY_OPTIONS {
  AVAILABLE = 'txt_available',
  NOT_AVAILABLE = 'not_available',
  DELETED = 'deleted',
}

export enum STATUS_FILTER {
  ALL = 'all',
  ACTIVE = 'active_correct',
  INACTIVE = 'inactive_correct',
  PLANNED = 'planned',
  EXPIRED = 'expired',
}

export enum VALIDITY_PERIOD {
  DAYS = 'days',
  WEEKS = 'weeks',
  MONTHS = 'months',
  FOREVER = 'forever',
}

export enum BUTTON_TITLE {
  CONFIRM = 'txt_confirm',
  CANCEL = 'txt_cancel',
  LEAVE = 'leave',
  DELETE = 'txt_delete',
  YES = 'txt_yes',
  NO = 'txt_no',
  OK = 'txt_ok',
  SET_BUSY = 'set_busy',
  STAY = 'txt_stay',
  SET_DEFAULT = 'txt_set_as_default',
  RESET = 'reset',
  SAVE = 'txt_save',
  CREATE = 'create',
  UPDATE = 'txt_update',
  CLOSE = 'close',
  ADD = 'add',
}

export enum MEASURE {
  NULL = '',
  GR = 'g',
  KG = 'kg',
  ML = 'ml',
  L = 'l',
  PIECES = 'pi',
  PEOPLE = 'pe',
  // KCAL = 'kcal',
  CAN = 'can',
  PLATE = 'plate',
  CUP = 'cup',
  // KCAL_GR = 'kcal_100_gm',
  WHOLE_PIECE = 'whole_piece',
  HALF_PIECE = '1_2_piece',
  QUARTER_PIECE = '1_4_piece',
}

export enum MEASURE_NUTRIENT {
  GR = 'g',
  ML = 'ml',
  PORTION = 'portion',
}
export const measureValues: MEASURE[] = Object.values(MEASURE);

export enum ITEM_TYPE {
  ITEM = 'item',
  ITEM_SIZE = 'item_size',
  MODIFIER = 'modifier',
  MODIFIER_OPTION = 'modifier_option',
  EXCLUSION = 'exclusion',
  CATEGORY = 'category',
  DEAL_COMBO = 'deal_combo',
  MEAL_COMBO = 'meal_combo',
  MEAL_SIZE = 'meal_size',
  MEAL_GROUP = 'meal_group',
  MEAL_CHOICE = 'meal_choice',
}

export const defaultValidation: IValidation = {
  isValid: true,
  errorText: undefined,
};

export const defaulLangTextValidation: ILangTextValidation = {
  enValid: true,
  enErrorText: undefined,
  arValid: true,
  arErrorText: undefined,
};

export const defaultTTranslate: TTranslate = {
  en: '',
  ar: '',
};

export const updateModalIntervalByLevel = {
  [UpdateLevel.Low]: 1000 * 60 * 60 * 24,
  [UpdateLevel.Medium]: 1000 * 60 * 60 * 2,
  [UpdateLevel.High]: 1000 * 60 * 5,
};
